// Remembers, per user and browser, which newly included pages the user has opened, so their green
// dots stay cleared.

import { NEWLY_INCLUDED_PAGES_SEEN_KEY } from 'app/utils/seenStorageKeys';

const SEEN_EVENT = 'newly-included-page-seen';

const storageKey = (userId: string) => `${NEWLY_INCLUDED_PAGES_SEEN_KEY}:${userId}`;

/** The stored value as it is, so a `useSyncExternalStore` snapshot stays stable between reads. */
export function readSeenPages(userId: string): string | null {
  try {
    return localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
}

export function parseSeenPages(stored: string | null): string[] {
  try {
    const pageIds: unknown = JSON.parse(stored ?? '[]');
    if (!Array.isArray(pageIds)) return [];

    return pageIds.filter((pageId): pageId is string => typeof pageId === 'string');
  } catch {
    return [];
  }
}

export function markPageSeen(userId: string, pageId: string): void {
  const seen = parseSeenPages(readSeenPages(userId));
  if (seen.includes(pageId)) return;

  try {
    localStorage.setItem(storageKey(userId), JSON.stringify([...seen, pageId]));
  } catch {
    return;
  }

  window.dispatchEvent(new Event(SEEN_EVENT));
}

export function subscribeToSeenPages(onChange: () => void): () => void {
  window.addEventListener(SEEN_EVENT, onChange);
  // Opening the page in another tab clears the dot here too.
  window.addEventListener('storage', onChange);

  return () => {
    window.removeEventListener(SEEN_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}
